import { useMemo } from 'react';
import { ActionableNotification, InlineNotification } from '@carbon/react';
import { useAtomValue } from 'jotai';
import classNames from 'classnames';
import { OutputSection } from 'components/output-section/output-section';
import {
  RescatSlotCountChart,
  RescatSlotCountTable,
  filterRescatSlotCount,
} from 'components/rescat-slot-count';
import {
  activeFilterCountAtom,
  displayAggregationAtom,
  mapScopeAtom,
  serverFiltersAtom,
} from './atoms';
import { useRegisterChartExport } from './chart-export-registry';
import { useEval3Page } from './context';
import { useCalcDetail, useCalcLifecycle, useEvalResult } from './hooks';
import { aggregationUnit } from './utils';
import styles from './slot-count.module.scss';

/**
 * Connected Slot Count section — the reserve-category roll-forward chart + table, reusing
 * the shared `RescatSlotCountChart` / `RescatSlotCountTable` (relocated from the legacy
 * eval-3 aggregated-free-cash-flow). Reads page identity + the viewed eval's calc-detail
 * (for `status`) + the dataset filters / spatial scope / display aggregation, fetches
 * `useEvalResult`, and feeds its `rescatSlotCount` slice into both components. Mirrors the
 * connected `PackageOverviewSection`; gated by the Layout Settings `slotCount` toggle at
 * the mount site.
 *
 * States — the chart + table are the PERMANENT fixture; chrome layers above them:
 *  - **Loading** — `isFetching || useCalcLifecycle.isRunning`: on a FIRST load (no data
 *    yet) the table shows its skeleton and the chart a loading/empty state; on a REFETCH
 *    over existing data the fixture DIMS in place instead of swapping back to a skeleton
 *    (matches the sibling sections). `aria-busy` carries the a11y signal either way.
 *  - **Error** — a notification + Retry ABOVE the fixture (never replaces it), shown only
 *    when NOT loading (a run/refetch in flight supersedes a stale error).
 *  - **Loaded** — the chart + table, with a "Filtered view" banner when a filter/scope
 *    is active.
 *
 * @return {JSX.Element} The Slot Count section.
 */
export function SlotCountSection() {
  const { calculationId } = useEval3Page();
  const { data: calcDetail } = useCalcDetail(calculationId);
  const aggregationKey = useAtomValue(displayAggregationAtom);
  const filters = useAtomValue(serverFiltersAtom);
  const mapScope = useAtomValue(mapScopeAtom);
  const activeFilterCount = useAtomValue(activeFilterCountAtom);

  const status = calcDetail?.status;
  const { data, isFetching, error, refetch } = useEvalResult({
    calcId: calculationId,
    status,
    aggregationKey,
    filters,
    mapScope,
  });

  // The chart's x-axis interval is a moment unit; `aggregationUnit` maps the display
  // aggregation ('monthly'|'quarterly'|'yearly') to the exact `AggregationPeriod` the
  // chart expects. eval3-new's response carries no `interval`, but `useEvalResult` sends
  // `aggregationKey`, so this equals the aggregation the data is in (parity with the
  // legacy view's `resultData.interval`).
  const interval = aggregationUnit(aggregationKey);
  // Window to the same first-N intervals the legacy eval-3 view shows — the shared
  // `filterRescatSlotCount` is the one source both paths use, so the rendered window is
  // identical (visual parity). NOT the relative/absolute display range (rescat never
  // used those, in legacy or here). Memoized (like the legacy view's
  // `filteredRescatSlotCount`) so the `memo`'d chart keeps a stable array identity
  // across unrelated re-renders.
  // No calc (workspace / draft): blank explicitly — `keepPreviousData` would otherwise
  // hold the previously-viewed calc's numbers even though the query is disabled.
  const rescatSlotCount =
    calculationId === null
      ? undefined
      : data?.results[0]?.result?.rescatSlotCount;
  const slotCount = useMemo(
    () => filterRescatSlotCount(rescatSlotCount ?? [], interval),
    [rescatSlotCount, interval]
  );

  // The unified loading umbrella: this section's own eval_result fetch OR the shared
  // calc-lifecycle wait (detail resolving / BE processing) — one loading language.
  const { isRunning } = useCalcLifecycle(calculationId);
  const isLoading = isFetching || isRunning;
  const hasData = slotCount.length > 0;

  // Register the Reserves chart for content export (group 'reserves'). The section is
  // always mounted (CSS-hidden when its Layout toggle is off), and Highcharts captures
  // offscreen from the chart's options — so Reserves stays exportable even when hidden
  // (FR9: always-export). `reserves_evolution` is the fixed BE `charts[key]`.
  const onExporterReady = useRegisterChartExport({
    id: 'reserves_evolution',
    name: 'Reserves Category Evolution',
    group: 'reserves',
  });

  return (
    <OutputSection
      title="Reserves Category Evolution"
      isLoading={isLoading}
      defaultExpanded
    >
      {activeFilterCount > 0 && (
        <InlineNotification
          className={styles.banner}
          kind="info"
          lowContrast
          hideCloseButton
          title="Filtered view"
          subtitle="Showing results based on the filters applied in the left panel."
        />
      )}
      {error && !isLoading && (
        <ActionableNotification
          className={styles.banner}
          inline
          kind="error"
          // hasFocus off: its auto-focus scrolls the errored section into view (page jump)
          hasFocus={false}
          lowContrast
          hideCloseButton
          title="Couldn't load the results"
          subtitle={error.message}
          actionButtonLabel="Retry"
          onActionButtonClick={() => refetch()}
        />
      )}
      {/* First load (no data) → the components show their own skeleton; refetch over
              existing data → dim the fixture in place (don't swap back to a skeleton). */}
      <div
        className={classNames(styles.content, {
          [styles.dimmed]: isLoading && hasData,
        })}
        aria-busy={isLoading}
      >
        <RescatSlotCountChart
          slotCount={slotCount}
          interval={interval}
          isLoading={isLoading && !hasData}
          onExporterReady={onExporterReady}
        />
        <RescatSlotCountTable
          slotCount={slotCount}
          isLoading={isLoading && !hasData}
        />
      </div>
    </OutputSection>
  );
}
